import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Rocket, TrendingUp, Smartphone, Check, Quote } from "lucide-react";
import { Section, Reveal, Eyebrow } from "@/components/primitives";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/apiClient";

const services = [
  {
    icon: Rocket,
    title: "Il tuo sito internet",
    desc: "Creiamo il sito della tua attività: bello, semplice e che si vede bene anche sul telefono. Prima ti mostriamo una bozza gratis.",
    points: ["Bozza gratuita", "Si vede bene sul telefono", "Ti trovano su Google"],
  },
  {
    icon: TrendingUp,
    title: "Farti trovare su Google",
    desc: "Ti aiutiamo a comparire quando le persone cercano un'attività come la tua. Più visite al sito, più clienti.",
    points: ["Pubblicità su Google", "Più clienti dal web", "Spiegato in modo semplice"],
  },
  {
    icon: Smartphone,
    title: "I tuoi social",
    desc: "Gestiamo Facebook e Instagram al posto tuo: foto, post e risposte ai clienti. Tu pensi al lavoro, ai social pensiamo noi.",
    points: ["Post pubblicati per te", "Facebook e Instagram", "Più persone ti conoscono"],
  },
];

const reasons = [
  { title: "Parliamo chiaro", desc: "Niente paroloni difficili. Ti spieghiamo tutto con parole semplici, passo dopo passo." },
  { title: "Bozza gratis", desc: "Prima ti facciamo vedere come sarà il tuo sito. Se ti piace andiamo avanti. Senza impegno." },
  { title: "Prezzi giusti", desc: "Costi chiari e trasparenti, adatti a ogni realtà. Nessuna sorpresa, nessun costo nascosto." },
  { title: "Facciamo tutto noi", desc: "Tu pensi alla tua attività, al sito pensiamo noi. Dall'inizio alla fine." },
];

const testimonials = [
  {
    name: "Giulia Ferraro",
    role: "Pasticceria Dolce Vita",
    text: "Non avevo mai avuto un sito. Me l'hanno fatto vedere prima, gratis, e mi è piaciuto subito. Ora i clienti mi trovano su Google.",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop&q=80",
  },
  {
    name: "Marco Bianchi",
    role: "Bianchi Costruzioni",
    text: "Pensavo fosse complicato e costoso. Invece mi hanno spiegato tutto con parole semplici e oggi abbiamo un sito che dà valore all'azienda.",
    avatar: "https://images.unsplash.com/photo-1679679811837-c28b2586f533?w=200&h=200&fit=crop&q=80",
  },
  {
    name: "Sara Conti",
    role: "Studio Conti · Commercialista",
    text: "Cercavo un'immagine online più professionale per il mio studio. Mi hanno seguita in tutto, con competenza e senza farmi perdere tempo.",
    avatar: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200&h=200&fit=crop&q=80",
  },
];

export default function Home() {
  const [stats] = useState([
    { value: "Gratis", label: "La prima bozza" },
    { value: "Senza", label: "Impegno" },
    { value: "1 ora", label: "Per vederla" },
  ]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <Section className="pt-20 pb-24 lg:pt-28 lg:pb-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Eyebrow>Il digitale fatto a regola d'arte</Eyebrow>
              <h1 className="mt-6 font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink">
                La tua attività<br />merita un <span className="text-coral">sito internet</span>.
              </h1>
              <p className="mt-6 text-lg text-ink/70 max-w-md leading-relaxed">
                Aiutiamo aziende e professionisti a farsi trovare online con un
                sito fatto come si deve. Ti mostriamo una bozza gratis e senza
                impegno: poi decidi tu.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contatti"
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center justify-center gap-2 bg-coral text-white rounded-full px-8 py-4 font-semibold hover:bg-[#e04047] transition-colors"
                >
                  Richiedi la tua bozza gratis
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/servizi"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center justify-center gap-2 border border-ink/20 text-ink rounded-full px-8 py-4 font-semibold hover:border-ink transition-colors"
                >
                  Come funziona
                </Link>
              </div>
              <div className="mt-12 flex gap-10">
                {stats.map((s) => (
                  <div key={s.label}>
                    <div className="font-display font-black text-3xl text-ink">{s.value}</div>
                    <div className="text-sm text-ink/50">{s.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative"
          >
            <div className="rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.12)]">
              <img
                src="https://images.unsplash.com/photo-1542837336-d14bdf342f9b?w=1200&q=80"
                alt="Un sito internet moderno mostrato su un computer portatile"
                className="w-full h-[460px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-ink text-beige rounded-2xl p-6 shadow-xl max-w-[220px] hidden sm:block">
              <Logo reverse size="text-xl" />
              <p className="mt-2 text-xs uppercase tracking-[0.2em] text-beige/60">
                Il digitale fatto a regola d'arte
              </p>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* SERVICES */}
      <Section className="py-20">
        <Reveal>
          <div className="max-w-2xl">
            <Eyebrow>Cosa facciamo per te</Eyebrow>
            <h2 className="mt-4 font-display font-bold text-3xl lg:text-4xl tracking-tight text-ink">
              Tutto parte dal tuo sito internet.
            </h2>
          </div>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-8">
          {services.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1}>
              <div
                data-testid={`home-service-card-${i}`}
                className="bg-white rounded-2xl p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)] h-full hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="h-14 w-14 rounded-xl bg-coral/10 flex items-center justify-center text-coral">
                  <s.icon size={26} />
                </div>
                <h3 className="mt-6 font-display font-bold text-xl text-ink">{s.title}</h3>
                <p className="mt-3 text-ink/60 leading-relaxed">{s.desc}</p>
                <ul className="mt-5 space-y-2">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-sm text-ink/70">
                      <Check size={16} className="text-coral" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* WHY US — dark */}
      <div className="bg-ink text-beige mt-10">
        <Section className="py-24">
          <Reveal>
            <div className="max-w-2xl">
              <Eyebrow dark>Perché sceglierci</Eyebrow>
              <h2 className="mt-4 font-display font-bold text-3xl lg:text-4xl tracking-tight">
                Senza paroloni,<br />senza brutte sorprese.
              </h2>
            </div>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {reasons.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.08}>
                <div data-testid={`why-card-${i}`} className="border-t border-beige/15 pt-6">
                  <div className="text-coral font-display font-black text-2xl">0{i + 1}</div>
                  <h3 className="mt-4 font-semibold text-lg">{r.title}</h3>
                  <p className="mt-2 text-beige/60 text-sm leading-relaxed">{r.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      {/* TESTIMONIALS */}
      <Section className="py-24">
        <Reveal>
          <div className="max-w-2xl">
            <Eyebrow>Dicono di noi</Eyebrow>
            <h2 className="mt-4 font-display font-bold text-3xl lg:text-4xl tracking-tight text-ink">
              Aziende e professionisti che ci hanno scelto.
            </h2>
          </div>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-8">
          {testimonials.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1}>
              <div
                data-testid={`testimonial-${i}`}
                className="bg-white rounded-2xl p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)] h-full flex flex-col"
              >
                <Quote className="text-coral" size={28} />
                <p className="mt-4 text-ink/80 leading-relaxed flex-1">"{t.text}"</p>
                <div className="mt-6 flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="h-12 w-12 rounded-full object-cover" />
                  <div>
                    <div className="font-semibold text-ink text-sm">{t.name}</div>
                    <div className="text-xs text-ink/50">{t.role}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section className="pb-24">
        <Reveal>
          <div className="bg-coral rounded-3xl px-8 py-16 lg:p-20 text-center text-white relative overflow-hidden">
            <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tight max-w-3xl mx-auto leading-tight">
              Vuoi vedere com'è il sito della tua attività?
            </h2>
            <p className="mt-5 text-white/85 text-lg max-w-xl mx-auto">
              Te lo facciamo vedere gratis e senza impegno. Se ti piace, andiamo avanti.
            </p>
            <Link
              to="/contatti"
              data-testid="home-final-cta"
              className="mt-9 inline-flex items-center gap-2 bg-white text-ink rounded-full px-8 py-4 font-semibold hover:bg-beige transition-colors"
            >
              Richiedi la tua bozza gratis <ArrowRight size={18} />
            </Link>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
